import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowRight,
  ArrowLeft,
  Plus,
  Minus,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_builder-hub-690/artifacts/p5nr9ybv_Elionyx%20logo.png";

/* ---------- Reveal-on-scroll helper ---------- */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ---------- Navigation ---------- */
const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "services", label: "Services" },
    { id: "approach", label: "Approach" },
    { id: "work", label: "Work" },
    { id: "industries", label: "Industries" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <header
      data-testid="site-nav"
      className="nav-blur fixed top-0 inset-x-0 z-50 border-b border-[var(--line)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand"
          className="flex items-center gap-3 group"
          aria-label="Elionyx Technologies LLP — Home"
        >
          <span
            className="block h-10 w-10 border border-[var(--line)] bg-white"
            role="img"
            aria-label="Elionyx Technologies logo"
            style={{
              backgroundImage: `url(${LOGO_URL})`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
          <span className="font-display text-[22px] leading-none tracking-tighter">
            Elionyx<span className="text-[var(--klein)]">.</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-10 text-sm">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="link-line text-[var(--fg)]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 text-sm font-medium border border-[var(--fg)] px-4 py-2 hover:bg-[var(--fg)] hover:text-[var(--bg)] transition-colors"
        >
          Start a project <ArrowUpRight className="w-4 h-4" />
        </a>
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[var(--line)] bg-[var(--bg)]"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="py-2 text-sm"
                data-testid={`mobile-link-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta"
              className="mt-2 inline-flex items-center gap-2 text-sm border border-[var(--fg)] px-4 py-2 w-fit"
            >
              Start a project <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------- Clients (placeholder logos) ---------- */
const CLIENTS = [
  { name: "Client One", sub: "Banking" },
  { name: "Client Two", sub: "Manufacturing" },
  { name: "Client Three", sub: "Retail & CPG" },
  { name: "Client Four", sub: "Energy" },
  { name: "Client Five", sub: "Healthcare" },
  { name: "Client Six", sub: "Logistics" },
  { name: "Client Seven", sub: "Telecom" },
  { name: "Client Eight", sub: "Public Sector" },
];

const Clients = () => {
  return (
    <section
      id="clients"
      data-testid="clients-section"
      className="border-b border-[var(--line)] py-16 lg:py-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end mb-10 lg:mb-14">
          <div className="col-span-12 lg:col-span-4">
            <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ Trusted by ]
            </span>
            <h2
              data-testid="clients-headline"
              className="mt-4 font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tighter reveal"
            >
              Operators
              <br />
              <span className="italic">we work alongside.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <p className="text-[var(--muted)] text-sm lg:text-base leading-relaxed max-w-xl">
              A selection of enterprises and scale-ups that have engaged Elionyx
              across data, AI, SAP and sourcing engagements.{" "}
              <span className="font-mono-tech text-xs text-[var(--klein)]">
                [ logos coming soon ]
              </span>
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 border-l border-t border-[var(--line)]"
          data-testid="clients-grid"
        >
          {CLIENTS.map((c, i) => (
            <div
              key={i}
              data-testid={`client-${i}`}
              className="border-r border-b border-[var(--line)] aspect-[4/3] sm:aspect-square flex flex-col items-center justify-center text-center px-3 group hover:bg-[var(--soft)] transition-colors"
            >
              <div className="font-display text-lg lg:text-xl tracking-tight text-[var(--fg)] opacity-50 group-hover:opacity-100 transition-opacity">
                {c.name}
              </div>
              <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-widest text-[var(--muted)]">
                {c.sub}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Case Studies ---------- */
const CASE_STUDIES = [
  {
    slug: "global-bank-risk-data-platform",
    tag: "Data Platform",
    industry: "Global Bank",
    year: "2025",
    title:
      "Consolidated 14 risk data sources into a single lakehouse — cut report latency from days to minutes.",
    metric: { k: "92%", v: "Faster regulatory reporting" },
    cover:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80",
    duration: "11 months",
    team: "1 architect + 2 squads (10 FTE)",
    location: "London / Bengaluru",
    stack: ["Databricks", "Delta Lake", "dbt", "Airflow", "Tableau", "Azure"],
    challenge:
      "The client's risk function was producing FRTB and IFRS9 reports from 14 disparate sources — three regional warehouses, four trading systems, and seven flat-file feeds. Each monthly run took 3–4 days of manual reconciliation and a further 36 hours of computation, leaving no time to investigate exceptions before regulatory submission.",
    approach:
      "We delivered a lakehouse foundation on Databricks with a medallion architecture, replacing point-to-point feeds with declarative ingestion. A semantic layer built in dbt exposed certified risk datasets, and Airflow orchestrated end-to-end runs with full lineage and data-quality checks at every hop. Tableau dashboards on top of the gold layer gave risk analysts exception views they could query interactively.",
    outcome:
      "Monthly close went from 4.5 days to under 6 hours. Reconciliation FTEs were redeployed to exception investigation. The platform now serves 9 downstream consumers including capital planning and stress-testing, and has been audited by two tier-1 regulators without findings.",
    metrics: [
      { k: "92%", v: "Faster regulatory reporting" },
      { k: "14 → 1", v: "Data sources unified" },
      { k: "9", v: "Downstream consumers" },
    ],
  },
  {
    slug: "retail-ai-replenishment-copilot",
    tag: "Applied AI",
    industry: "Retail (Top-5 EU)",
    year: "2025",
    title:
      "Built a forecasting + replenishment co-pilot serving 1,200 stores with hourly inference.",
    metric: { k: "18%", v: "Reduction in stock-outs" },
    cover:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80",
    duration: "8 months",
    team: "1 ML lead + 1 squad (6 FTE)",
    location: "Amsterdam / Hyderabad",
    stack: ["GCP Vertex AI", "BigQuery", "Feature Store", "FastAPI", "React"],
    challenge:
      "A pan-European retailer was losing an estimated €40M annually to stock-outs in fast-moving categories. Their statistical forecast (ARIMA + manual overrides) couldn't handle promo cannibalisation, weather shocks, or the long-tail SKUs that drive 30% of basket value.",
    approach:
      "We built an ensemble forecaster (gradient-boosted trees + temporal fusion transformer) running on Vertex AI, scoring 1.2M SKU-store-day combinations hourly. A replenishment co-pilot surfaced the top recommendations to category managers with explanations and a one-click override path. Every override fed back into the next training run.",
    outcome:
      "Stock-outs fell 18% within the first quarter post-rollout. Forecast bias improved 41% on promo SKUs. The co-pilot is now part of the standard category-manager workflow and the override loop has become the team's primary feedback mechanism for the AI.",
    metrics: [
      { k: "18%", v: "Reduction in stock-outs" },
      { k: "41%", v: "Better promo forecasting" },
      { k: "1.2M", v: "Predictions per hour" },
    ],
  },
  {
    slug: "industrial-s4hana-greenfield",
    tag: "SAP Services",
    industry: "Industrial Manufacturer",
    year: "2024",
    title:
      "Greenfield S/4HANA roll-out across 9 plants and 23 country org units in 14 months.",
    metric: { k: "0", v: "Critical defects at go-live" },
    cover:
      "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&w=1600&q=80",
    duration: "14 months",
    team: "2 architects + 4 squads (28 FTE)",
    location: "Stuttgart / Pune",
    stack: ["SAP S/4HANA 2023", "SAP BTP", "CPI", "Fiori", "Solman"],
    challenge:
      "A mid-sized industrial group running ECC 6.0 across acquired entities had 9 different chart-of-accounts variants and incompatible material masters. Their previous integrator's roadmap projected 28 months and €38M; the board wanted the same outcome in less than half the time and budget.",
    approach:
      "We led a fit-to-standard programme with a single global template, a harmonised CoA, and a master-data factory that cleansed 240k material records before any cutover. Roll-outs followed a fixed 6-week-per-country cadence with a centralised hypercare squad, and integration to legacy MES was handled through SAP BTP rather than custom Z-objects.",
    outcome:
      "All 9 plants and 23 country units went live in 14 months with zero P1 defects at any cutover. Day-1 month-end close ran in 4 days vs. 11 in the legacy estate. The standard template is now the basis for two further M&A integrations.",
    metrics: [
      { k: "0", v: "Critical defects at go-live" },
      { k: "14", v: "Months end-to-end" },
      { k: "9 + 23", v: "Plants + country units live" },
    ],
  },
];

const CaseStudies = () => {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="border-b border-[var(--line)] py-20 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="col-span-12 lg:col-span-6">
            <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ 04 — Selected work ]
            </span>
            <h2
              data-testid="work-headline"
              className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter reveal"
            >
              Outcomes,
              <br />
              <span className="italic">not impressions.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <p className="text-[var(--muted)] text-base leading-relaxed">
              A snapshot of engagements where Elionyx led delivery end-to-end.
              Full anonymised case studies available under NDA.{" "}
              <span className="font-mono-tech text-xs text-[var(--klein)]">
                [ placeholders ]
              </span>
            </p>
          </div>
        </div>

        <div className="mt-12 lg:mt-16 grid lg:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
          {CASE_STUDIES.map((cs, i) => (
            <Link
              key={cs.slug}
              to={`/work/${cs.slug}`}
              data-testid={`case-study-${i}`}
              className="bg-[var(--soft)] flex flex-col group hover:bg-[var(--fg)] transition-colors duration-500"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[var(--soft)]">
                <img
                  src={cs.cover}
                  alt={`${cs.industry} — ${cs.tag}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>

              <div className="p-8 lg:p-10 flex flex-col flex-1">
                <div className="flex items-center justify-between text-xs font-mono-tech uppercase tracking-wider text-[var(--muted)] group-hover:text-[var(--bg)]/60 transition-colors">
                  <span className="text-[var(--amber)] group-hover:text-[var(--amber-soft)]">{cs.tag}</span>
                  <span>{cs.year}</span>
                </div>

                <div
                  className="mt-6 font-mono-tech text-xs uppercase tracking-wider text-[var(--klein)] group-hover:text-[var(--klein-soft)] transition-colors"
                  data-testid={`case-industry-${i}`}
                >
                  {cs.industry}
                </div>

                <h3
                  data-testid={`case-title-${i}`}
                  className="mt-3 font-display font-light text-2xl lg:text-[28px] leading-[1.15] tracking-tight text-[var(--fg)] group-hover:text-[var(--bg)] transition-colors"
                >
                  {cs.title}
                </h3>

                <div className="mt-auto pt-8">
                  <div className="pt-6 border-t border-[var(--line)] group-hover:border-[var(--bg)]/15 transition-colors flex items-end justify-between">
                    <div>
                      <div className="font-display tick text-4xl lg:text-5xl tracking-tighter text-[var(--fg)] group-hover:text-[var(--bg)] transition-colors">
                        {cs.metric.k}
                      </div>
                      <div className="mt-1 text-xs font-mono-tech uppercase tracking-wider text-[var(--muted)] group-hover:text-[var(--bg)]/60 transition-colors">
                        {cs.metric.v}
                      </div>
                    </div>
                    <ArrowUpRight
                      className="w-6 h-6 text-[var(--fg)] group-hover:text-[var(--brand-blue)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                      data-testid={`case-arrow-${i}`}
                    />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3 items-center">
          <a
            href="#contact"
            className="btn-ghost"
            data-testid="work-cta-request"
          >
            Request a full case study
          </a>
          <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
            More work under NDA — available on request
          </span>
        </div>
      </div>
    </section>
  );
};

/* ---------- Hero ---------- */
const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-44 lg:pb-32 border-b border-[var(--line)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12">
            <h1
              data-testid="hero-headline"
              className="font-display font-light text-[40px] sm:text-[60px] lg:text-[96px] xl:text-[112px] leading-[0.98] tracking-tighter"
            >
              We engineer
              <br />
              <span className="italic font-normal">intelligent</span> enterprises —
              <br />
              with a&nbsp;
              <span className="italic font-normal text-[var(--klein)] ml-2 lg:ml-3">
                human touch.
              </span>
            </h1>

            <div className="mt-12 lg:mt-16 grid grid-cols-12 gap-6">
              <p
                data-testid="hero-sub"
                className="col-span-12 md:col-span-7 text-[var(--muted)] text-base lg:text-lg leading-relaxed max-w-2xl"
              >
                Elionyx Technologies LLP is a systems integrator partnering with
                ambitious organisations to ship data platforms, AI products,
                and SAP transformations — without the consultancy theatre.
              </p>
              <div className="col-span-12 md:col-span-5 flex md:justify-end items-end gap-3">
                <a href="#contact" data-testid="hero-cta-primary" className="btn-primary">
                  Start a project <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#services" data-testid="hero-cta-secondary" className="btn-ghost">
                  View services
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-20 lg:mt-32">
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[var(--line)]">
          {[
            { k: "120+", v: "Engagements delivered" },
            { k: "40+", v: "Active enterprise clients" },
            { k: "9", v: "Industries served" },
            { k: "24/7", v: "Managed operations" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="border-r border-b border-[var(--line)] p-6 lg:p-8"
            >
              <div className="font-display text-4xl lg:text-5xl tick tracking-tighter">
                {s.k}
              </div>
              <div className="mt-3 text-xs font-mono-tech uppercase tracking-wider text-[var(--muted)]">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Marquee ---------- */
const Marquee = () => {
  const items = [
    "Data Engineering",
    "Applied AI",
    "SAP S/4HANA",
    "Strategic Sourcing",
    "Cloud Platforms",
    "MLOps",
    "Procurement Transformation",
    "Analytics",
  ];
  const loop = [...items, ...items, ...items];
  return (
    <div
      data-testid="marquee"
      className="border-b border-[var(--line)] overflow-hidden py-6"
    >
      <div className="marquee-track flex gap-12 whitespace-nowrap font-mono-tech text-sm uppercase tracking-widest text-[var(--muted)]">
        {loop.map((t, i) => (
          <span key={i} className="flex items-center gap-12">
            {t}
            <span className="w-2 h-2 bg-[var(--klein)] inline-block" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Services ---------- */
const SERVICES = [
  {
    n: "01",
    title: "Data Engineering & Analytics",
    desc:
      "Lakehouse architectures, real-time pipelines and analytics products that turn fragmented data into a defensible asset.",
    capabilities: [
      "Lakehouse on Databricks / Snowflake",
      "Streaming with Kafka, Flink",
      "dbt + Airflow orchestration",
      "Self-serve BI & semantic layers",
    ],
  },
  {
    n: "02",
    title: "AI",
    desc:
      "Production-grade applied AI — from RAG systems and copilots to forecasting and decision automation, with guardrails and evals.",
    capabilities: [
      "Enterprise RAG & agents",
      "MLOps and model evaluation",
      "Forecasting & optimisation",
      "Vision & document intelligence",
    ],
  },
  {
    n: "03",
    title: "SAP Services",
    desc:
      "Pragmatic SAP delivery across S/4HANA, BTP and adjacent modules — implementation, migration, and steady-state managed services.",
    capabilities: [
      "S/4HANA implementation & roll-outs",
      "ECC → S/4 migrations (brownfield / greenfield)",
      "SAP BTP & integration",
      "AMS and managed operations",
    ],
  },
  {
    n: "04",
    title: "Strategic Sourcing",
    desc:
      "Sourcing and procurement transformation — category strategy, supplier rationalisation, and digital procurement platforms.",
    capabilities: [
      "Category & spend strategy",
      "Supplier consolidation & negotiation",
      "Source-to-Pay platform enablement",
      "Procurement analytics",
    ],
  },
];

const Services = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="services"
      data-testid="services-section"
      className="border-b border-[var(--line)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-32 pb-6">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="col-span-12 lg:col-span-2">
            <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ 02 — Services ]
            </span>
          </div>
          <div className="col-span-12 lg:col-span-10">
            <h2
              data-testid="services-headline"
              className="font-display font-light text-4xl sm:text-5xl lg:text-7xl tracking-tighter reveal"
            >
              Four practices.
              <br />
              <span className="italic">One operating model.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="border-t border-[var(--line)]">
          {SERVICES.map((s, i) => {
            const open = openIdx === i;
            return (
              <div
                key={s.n}
                data-testid={`service-row-${i}`}
                className="wipe-row border-b border-[var(--line)]"
              >
                <button
                  className="w-full text-left grid grid-cols-12 gap-4 lg:gap-8 items-start py-8 lg:py-10 px-2"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`service-toggle-${i}`}
                >
                  <div className="col-span-2 lg:col-span-1 font-mono-tech wipe-num text-sm pt-2">
                    {s.n}
                  </div>
                  <div className="col-span-10 lg:col-span-6">
                    <div className="wipe-text font-display font-light text-2xl sm:text-3xl lg:text-5xl tracking-tighter">
                      {s.title}
                    </div>
                  </div>
                  <div className="col-span-10 lg:col-span-4 wipe-desc text-[var(--muted)] text-sm lg:text-base leading-relaxed">
                    {s.desc}
                  </div>
                  <div className="col-span-2 lg:col-span-1 flex justify-end pt-2">
                    <ArrowUpRight className="wipe-arrow w-6 h-6" />
                  </div>
                </button>

                {open && (
                  <div
                    data-testid={`service-detail-${i}`}
                    className="grid grid-cols-12 gap-4 lg:gap-8 px-2 pb-10"
                  >
                    <div className="hidden lg:block col-span-1" />
                    <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                      {s.capabilities.map((c, k) => (
                        <div
                          key={k}
                          className="flex items-center gap-3 text-sm wipe-text"
                          data-testid={`service-cap-${i}-${k}`}
                        >
                          <Check className="w-4 h-4 text-[var(--klein)]" />
                          {c}
                        </div>
                      ))}
                    </div>
                    <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
                      <a
                        href="#contact"
                        className="link-line text-sm font-medium wipe-text"
                        data-testid={`service-link-${i}`}
                      >
                        Discuss this practice →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Approach ---------- */
const Approach = () => {
  const steps = [
    {
      n: "/01",
      t: "Diagnose",
      d: "Weeks, not months. We map your systems, data and process gaps against business outcomes before writing a line of code.",
    },
    {
      n: "/02",
      t: "Architect",
      d: "Reference architectures that are vendor-neutral and pragmatic. We optimise for total cost of ownership and team autonomy.",
    },
    {
      n: "/03",
      t: "Deliver",
      d: "Small, senior squads. Two-week increments. Production from day one with telemetry, evals and rollback paths in place.",
    },
    {
      n: "/04",
      t: "Operate",
      d: "Managed services with SLAs that matter — uptime, response times, and ongoing optimisation against your KPIs.",
    },
  ];
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="border-b border-[var(--line)] py-20 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-4">
            <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ 03 — Approach ]
            </span>
            <h2
              data-testid="approach-headline"
              className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter reveal"
            >
              No theatre.
              <br />
              <span className="italic">Just delivery.</span>
            </h2>
            <p className="mt-6 text-[var(--muted)] max-w-md leading-relaxed">
              We are engineers, architects and operators — not slide-makers.
              Every engagement is built around a single objective: get to
              production, prove value, then scale.
            </p>
            <div className="mt-10 aspect-[4/5] overflow-hidden bg-[var(--soft)] max-w-md">
              <img
                src="https://images.pexels.com/photos/9068369/pexels-photo-9068369.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Two consultants discussing in a modern office"
                loading="lazy"
                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
              />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <div className="grid sm:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
              {steps.map((s, i) => (
                <div
                  key={i}
                  data-testid={`approach-step-${i}`}
                  className="bg-[var(--soft)] p-8 lg:p-10 reveal"
                >
                  <div className="font-mono-tech text-sm text-[var(--amber)]">
                    {s.n}
                  </div>
                  <div className="mt-4 font-display text-2xl lg:text-3xl tracking-tighter">
                    {s.t}
                  </div>
                  <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed">
                    {s.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Industries ---------- */
const Industries = () => {
  const industries = [
    "Banking & Financial Services",
    "Manufacturing",
    "Retail & CPG",
    "Energy & Utilities",
    "Healthcare & Life Sciences",
    "Logistics & Supply Chain",
    "Telecommunications",
    "Public Sector",
    "Professional Services",
  ];
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="border-b border-[var(--line)] py-20 lg:py-32 bg-[var(--soft)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-5">
            <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ 05 — Industries ]
            </span>
            <h2
              data-testid="industries-headline"
              className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter reveal"
            >
              Built for
              <br />
              <span className="italic">regulated, complex</span>
              <br />
              environments.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="border-t border-[var(--fg)]">
              {industries.map((ind, i) => (
                <div
                  key={i}
                  data-testid={`industry-${i}`}
                  className="flex items-center justify-between border-b border-[var(--line)] py-5 group cursor-default"
                >
                  <div className="flex items-center gap-6">
                    <span className="font-mono-tech text-xs text-[var(--muted)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl lg:text-2xl tracking-tight">
                      {ind}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-[var(--klein)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Manifesto ---------- */
const Manifesto = () => {
  return (
    <section
      data-testid="manifesto-section"
      className="border-b border-[var(--line)] bg-[var(--soft)] py-24 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-10">
        <div className="col-span-12 lg:col-span-2">
          <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
            [ Manifesto ]
          </span>
        </div>
        <div className="col-span-12 lg:col-span-10">
          <p
            data-testid="manifesto-text"
            className="font-display font-light text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tighter text-[var(--fg)]"
          >
            We believe enterprise software should be{" "}
            <span className="italic text-[var(--muted)]">measured, not pitched.</span>{" "}
            That data is a verb. That AI without operations is a demo. And that
            a great integrator leaves a client{" "}
            <span className="text-[var(--klein-soft)]">more capable</span>, not more
            dependent.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQ = () => {
  const items = [
    {
      q: "How do engagements typically start?",
      a: "Most clients begin with a 2–4 week discovery — fixed price, fixed timeline — that produces a target architecture, a delivery plan and a clear business case before any committed multi-quarter spend.",
    },
    {
      q: "Do you work onshore, offshore, or hybrid?",
      a: "Hybrid by default. Senior architects and product leads engage onshore; delivery squads operate from our nearshore and offshore studios with full overlap windows.",
    },
    {
      q: "Are you tied to specific vendors or platforms?",
      a: "We are partnered with the major hyperscalers, SAP and the leading data platforms — but we are not resellers. We choose the stack that minimises your total cost of ownership.",
    },
    {
      q: "Can you take over an existing programme?",
      a: "Yes. A meaningful share of our work is recovery and stabilisation of in-flight programmes. We are comfortable with brownfield environments and inherited technical debt.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="border-b border-[var(--line)] py-20 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-10">
        <div className="col-span-12 lg:col-span-4">
          <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
            [ 06 — FAQ ]
          </span>
          <h2
            data-testid="faq-headline"
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter reveal"
          >
            Frequent
            <br />
            <span className="italic">questions.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-8">
          <div className="border-t border-[var(--fg)]">
            {items.map((it, i) => (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className="border-b border-[var(--line)]"
              >
                <button
                  className="w-full flex items-start justify-between gap-6 py-6 text-left"
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-xl lg:text-2xl tracking-tight">
                    {it.q}
                  </span>
                  {open === i ? (
                    <Minus className="w-5 h-5 mt-2 shrink-0 text-[var(--klein)]" />
                  ) : (
                    <Plus className="w-5 h-5 mt-2 shrink-0" />
                  )}
                </button>
                {open === i && (
                  <p
                    data-testid={`faq-answer-${i}`}
                    className="text-[var(--muted)] pb-6 pr-10 leading-relaxed text-[15px]"
                  >
                    {it.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Contact ---------- */
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-32 border-b border-[var(--line)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-10">
        <div className="col-span-12 lg:col-span-5">
          <span className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
            [ 07 — Contact ]
          </span>
          <h2
            data-testid="contact-headline"
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter reveal"
          >
            Tell us what
            <br />
            you&apos;re <span className="italic">trying to</span>
            <br />
            <span className="text-[var(--klein)]">build.</span>
          </h2>
          <div className="mt-10 space-y-6 text-sm">
            <div>
              <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)] mb-1">
                General enquiries
              </div>
              <a
                href="mailto:info@elionyxtech.com"
                className="link-line"
                data-testid="contact-email"
              >
                info@elionyxtech.com
              </a>
            </div>
            <div>
              <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)] mb-1">
                Registered office
              </div>
              <div className="text-[var(--fg)]">
                Elionyx Technologies LLP
                <br />
                India
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          {sent ? (
            <div
              data-testid="contact-success"
              className="border border-[var(--klein)] p-10 lg:p-14"
            >
              <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--klein)]">
                [ Received ]
              </div>
              <h3 className="mt-6 font-display text-3xl lg:text-4xl tracking-tighter">
                Thanks — your message is with us.
              </h3>
              <p className="mt-4 text-[var(--muted)] leading-relaxed max-w-lg">
                A partner from the relevant practice will reach out within one
                business day to schedule a 30-minute discovery call.
              </p>
              <button
                className="mt-8 btn-ghost"
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="grid grid-cols-2 gap-x-8 gap-y-2"
            >
              <div className="col-span-2 sm:col-span-1">
                <label className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  01 / Your name *
                </label>
                <input
                  className="field"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Jane Doe"
                  data-testid="contact-input-name"
                  required
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  02 / Work email *
                </label>
                <input
                  className="field"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="jane@company.com"
                  data-testid="contact-input-email"
                  required
                />
              </div>
              <div className="col-span-2 sm:col-span-1 mt-4">
                <label className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  03 / Company
                </label>
                <input
                  className="field"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Company Ltd."
                  data-testid="contact-input-company"
                />
              </div>
              <div className="col-span-2 sm:col-span-1 mt-4">
                <label className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  04 / Practice
                </label>
                <select
                  className="field"
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-input-service"
                >
                  <option value="">Select a practice</option>
                  <option>Data Engineering & Analytics</option>
                  <option>AI</option>
                  <option>SAP Services</option>
                  <option>Strategic Sourcing</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div className="col-span-2 mt-4">
                <label className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  05 / What are you trying to build? *
                </label>
                <textarea
                  className="field"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Outline the problem, current state, and any constraints we should know about."
                  rows={4}
                  data-testid="contact-input-message"
                  required
                />
              </div>
              <div className="col-span-2 mt-8 flex items-center justify-between flex-wrap gap-4">
                <div className="text-xs text-[var(--muted)] max-w-sm">
                  By submitting you agree we may contact you about your enquiry.
                  We never share your details.
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? "Sending…" : "Send message"}{" "}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[var(--soft)] text-[var(--fg)] border-t border-[var(--line)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-5">
            <a
              href="#top"
              data-testid="footer-brand"
              className="inline-flex items-center gap-4"
            >
              <span
                className="block h-16 w-16 bg-white border border-[var(--line)]"
                role="img"
                aria-label="Elionyx Technologies logo"
                style={{
                  backgroundImage: `url(${LOGO_URL})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
              <span className="font-display text-5xl lg:text-6xl tracking-tighter">
                Elionyx<span className="text-[var(--klein-soft)]">.</span>
              </span>
            </a>
            <p className="mt-6 text-[var(--muted)] max-w-md text-sm leading-relaxed">
              Elionyx Technologies LLP is a systems integrator engineering
              data, AI, SAP and sourcing transformations for enterprises that
              expect more from their delivery partners.
            </p>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)] mb-4">
              Practices
            </div>
            <ul className="space-y-2 text-sm">
              {SERVICES.map((s) => (
                <li key={s.n}>
                  <a href="#services" className="link-line text-[var(--fg)]">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)] mb-4">
              Company
            </div>
            <ul className="space-y-2 text-sm">
              <li><a href="#approach" className="link-line text-[var(--fg)]">Approach</a></li>
              <li><a href="#industries" className="link-line text-[var(--fg)]">Industries</a></li>
              <li><a href="#faq" className="link-line text-[var(--fg)]">FAQ</a></li>
              <li><a href="#contact" className="link-line text-[var(--fg)]">Contact</a></li>
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)] mb-4">
              Get in touch
            </div>
            <div className="text-sm space-y-2">
              <div>
                <a className="link-line text-[var(--fg)]" href="mailto:info@elionyxtech.com">
                  info@elionyxtech.com
                </a>
              </div>
            </div>
            <a
              href="#contact"
              data-testid="footer-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm border border-[var(--fg)] px-4 py-2 hover:bg-[var(--fg)] hover:text-[var(--bg)] transition-colors"
            >
              Start a project <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[var(--line)] flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-[var(--muted)] font-mono-tech uppercase tracking-wider">
          <div>© {new Date().getFullYear()} Elionyx Technologies LLP. All rights reserved.</div>
          <div className="flex gap-6">
            <span>Privacy</span>
            <span>Terms</span>
            <span>India · Remote-first</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------- Scroll-restore helper ---------- */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait for DOM to render the target section, then scroll
      const id = hash.replace("#", "");
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 80);
        }
      };
      tryScroll();
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);
  return null;
};

/* ---------- Case Study Detail Page ---------- */
const CaseStudyDetail = () => {
  useReveal();
  const { slug } = useParams();
  const cs = CASE_STUDIES.find((c) => c.slug === slug);
  const idx = CASE_STUDIES.findIndex((c) => c.slug === slug);
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];

  useEffect(() => {
    if (cs) document.title = `${cs.industry} · ${cs.tag} — Elionyx`;
  }, [cs]);

  if (!cs) {
    return (
      <div data-testid="case-study-not-found" className="min-h-screen flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-6 py-32 text-center">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ 404 — Not found ]
            </div>
            <h1 className="mt-6 font-display font-light text-5xl lg:text-7xl tracking-tighter">
              We can&apos;t find that case study.
            </h1>
            <Link
              to="/#work"
              className="btn-primary mt-10 inline-flex"
              data-testid="case-study-back-home"
            >
              <ArrowLeft className="w-4 h-4" /> Back to all work
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid={`case-study-page-${cs.slug}`} className="relative">
      <Nav />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-12 lg:pb-16 border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link
            to="/#work"
            data-testid="case-study-back-link"
            className="inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-wider text-[var(--muted)] hover:text-[var(--klein)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>

          <div className="mt-10 grid grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="flex items-center gap-4 text-xs font-mono-tech uppercase tracking-wider text-[var(--muted)]">
                <span>{cs.tag}</span>
                <span className="w-1.5 h-1.5 bg-[var(--klein)]" />
                <span>{cs.industry}</span>
                <span className="w-1.5 h-1.5 bg-[var(--klein)]" />
                <span>{cs.year}</span>
              </div>
              <h1
                data-testid="case-study-title"
                className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-[80px] leading-[0.98] tracking-tighter"
              >
                {cs.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Cover image */}
      <section className="border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <div className="aspect-[21/9] overflow-hidden bg-[var(--soft)]">
            <img
              src={cs.cover}
              alt={`${cs.industry} — ${cs.tag}`}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-[var(--line)]">
            {[
              { k: "Duration", v: cs.duration },
              { k: "Team", v: cs.team },
              { k: "Locations", v: cs.location },
              { k: "Stack", v: cs.stack.slice(0, 3).join(" · ") + (cs.stack.length > 3 ? " +" : "") },
            ].map((f, i) => (
              <div
                key={i}
                data-testid={`cs-fact-${i}`}
                className="border-r border-b border-[var(--line)] p-6 lg:p-8"
              >
                <div className="font-mono-tech text-[11px] uppercase tracking-wider text-[var(--muted)]">
                  {f.k}
                </div>
                <div className="mt-3 font-display text-lg lg:text-xl tracking-tight">
                  {f.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Body — Challenge / Approach / Outcome */}
      <section className="py-20 lg:py-28 border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {[
            { n: "01", t: "The Challenge", body: cs.challenge },
            { n: "02", t: "Our Approach", body: cs.approach },
            { n: "03", t: "The Outcome", body: cs.outcome },
          ].map((b, i) => (
            <div
              key={i}
              data-testid={`cs-body-${i}`}
              className={`grid grid-cols-12 gap-6 lg:gap-10 ${i > 0 ? "mt-16 lg:mt-24" : ""}`}
            >
              <div className="col-span-12 lg:col-span-3">
                <div className="font-mono-tech text-xs text-[var(--amber)]">
                  /{b.n}
                </div>
                <h3 className="mt-3 font-display font-light text-3xl lg:text-4xl tracking-tighter reveal">
                  {b.t}
                </h3>
              </div>
              <div className="col-span-12 lg:col-span-8 lg:col-start-5">
                <p className="text-[var(--fg)] text-lg lg:text-xl leading-relaxed reveal">
                  {b.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Metrics strip */}
      <section className="bg-[var(--soft)] text-[var(--fg)] border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
            [ Outcomes at a glance ]
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
            {cs.metrics.map((m, i) => (
              <div
                key={i}
                data-testid={`cs-metric-${i}`}
                className="bg-[var(--soft)] p-8 lg:p-10"
              >
                <div className="font-display tick text-5xl lg:text-7xl tracking-tighter text-[var(--fg)]">
                  {m.k}
                </div>
                <div className="mt-3 text-xs font-mono-tech uppercase tracking-wider text-[var(--muted)]">
                  {m.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stack */}
      <section className="py-16 lg:py-24 border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-3">
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ Stack ]
            </div>
            <h3 className="mt-3 font-display font-light text-3xl lg:text-4xl tracking-tighter">
              What we used.
            </h3>
          </div>
          <div className="col-span-12 lg:col-span-8 lg:col-start-5 flex flex-wrap gap-2">
            {cs.stack.map((s) => (
              <span
                key={s}
                className="font-mono-tech text-xs uppercase tracking-wider border border-[var(--line)] px-3 py-2 hover:border-[var(--klein)] hover:text-[var(--klein)] transition-colors"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Next + CTA */}
      <section className="py-20 lg:py-28 border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-6">
            <div className="font-mono-tech text-xs uppercase tracking-wider text-[var(--muted)]">
              [ Next case study ]
            </div>
            <Link
              to={`/work/${next.slug}`}
              data-testid="cs-next-link"
              className="mt-4 block group"
            >
              <h3 className="font-display font-light text-3xl lg:text-5xl tracking-tighter group-hover:text-[var(--klein)] transition-colors">
                {next.industry}
                <span className="block italic text-[var(--muted)] group-hover:text-[var(--klein-soft)] text-2xl lg:text-3xl mt-2">
                  {next.tag} · {next.year}
                </span>
              </h3>
              <div className="mt-4 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-wider text-[var(--klein)]">
                Read case <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex flex-col justify-end">
            <p className="text-[var(--muted)] text-base leading-relaxed">
              Working on something with a similar shape? We typically scope
              comparable engagements in a 2-week discovery sprint.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/#contact" data-testid="cs-cta-contact" className="btn-primary">
                Start a conversation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/#work" data-testid="cs-cta-back" className="btn-ghost">
                All work
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ---------- Home Page ---------- */
const Home = () => {
  useReveal();
  useEffect(() => {
    document.title = "Elionyx Technologies LLP — Systems Integrator";
  }, []);
  return (
    <div data-testid="home-page" className="relative">
      <Nav />
      <main className="pt-0">
        <Hero />
        <Marquee />
        <Clients />
        <Services />
        <Approach />
        <CaseStudies />
        <Industries />
        <Manifesto />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/work/:slug" element={<CaseStudyDetail />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
